import type { FilterState } from '../../../../../shared/components/FilterDrawer';
import type { DateRange } from '../../../../../shared/components/DurationSelector/types';
import type { MetricsFilters } from '../../../../../shared/api/types';

export const buildFilterParams = (
  filters: FilterState,
  dateRange: DateRange,
  clientId?: string | null,
  compareRange?: DateRange | null,
  /**
   * Second window the "reliable data only" filter must also find data in, so
   * both sides of a period comparison resolve to the same participants
   * (BT-101). Kept separate from `compareRange`, which also redefines the
   * change-percentage baseline — a data-quality toggle must not move trend
   * arrows.
   */
  reliableCompareRange?: DateRange | null,
  options?: { isCentralAdmin?: boolean },
): MetricsFilters => {
  const [startDate, endDate] = dateRange;
  const [compareStart, compareEnd] = compareRange ?? [null, null];
  // Only send a comparison window when BOTH ends are set, so a half-picked
  // range never overrides the default auto-previous-equivalent comparison.
  const hasCompare = Boolean(compareStart && compareEnd);

  const [reliableStart, reliableEnd] = reliableCompareRange ?? [null, null];
  const hasReliableCompare = Boolean(filters.reliableDataOnly && reliableStart && reliableEnd);

  return {
    start_date: startDate ? startDate.format('YYYY-MM-DD') : undefined,
    end_date: endDate ? endDate.format('YYYY-MM-DD') : undefined,
    compare_start_date: hasCompare && compareStart ? compareStart.format('YYYY-MM-DD') : undefined,
    compare_end_date: hasCompare && compareEnd ? compareEnd.format('YYYY-MM-DD') : undefined,
    region: filters.region.length > 0 ? filters.region : undefined,
    depot: filters.depot.length > 0 ? filters.depot : undefined,
    department: filters.department.length > 0 ? filters.department : undefined,
    age_range: filters.ageRange.length > 0 ? filters.ageRange : undefined,
    sex: filters.sex.length > 0 ? filters.sex : undefined,
    shift_pattern: filters.shiftPattern.length > 0 ? filters.shiftPattern : undefined,
    job_role: filters.jobRole.length > 0 ? filters.jobRole : undefined,
    user_ids: filters.searchUser.length > 0 ? filters.searchUser : undefined,
    client_id: clientId !== undefined ? clientId || null : undefined,
    // Omitted entirely when off, so the query key (and cache) for an unfiltered
    // dashboard is unchanged from before BT-101.
    reliable_data: filters.reliableDataOnly ? true : undefined,
    reliable_compare_start_date: hasReliableCompare && reliableStart ? reliableStart.format('YYYY-MM-DD') : undefined,
    reliable_compare_end_date: hasReliableCompare && reliableEnd ? reliableEnd.format('YYYY-MM-DD') : undefined,
  };
};

/** For the few consumers that must stay single-valued (e.g. an AI insights scope). */
export const firstSexValue = (sex: MetricsFilters['sex']): string | undefined => (Array.isArray(sex) ? sex[0] : sex);
